import {Injectable} from '@angular/core';
import {Selector, State} from '@ngxs/store';
import {ManagePlaylistsStateModel} from '@store/manage-playlists/manage-playlists-state.model';
import {IPlaylistMedia, IPlaylistStripe} from '@interfaces/playlist';
import {CdkDragDrop, copyArrayItem, moveItemInArray} from '@angular/cdk/drag-drop';
import {MediaTypeEnum} from '@shared/enums/media-type.enum';
import {PlaylistModel} from '../../models/playlist/playlist.model';
import {PlaylistChanelEnum} from '../../models/playlist/playlist-chanel.enum';
import {MatLegacyCheckboxChange as MatCheckboxChange} from '@angular/material/legacy-checkbox';
import {NgxsDataRepository} from '@angular-ru/ngxs/repositories';
import {StateRepository} from '@angular-ru/ngxs/decorators';
import {DataActionWithoutSubscription} from '@shared/functions';

@StateRepository()
@State<ManagePlaylistsStateModel>({
    name: 'managePlaylistsState',
    defaults: {
        droppedAudioMedia: [], // in drag n drop container
        droppedStripe: null,
        droppedVisualMedia: [],
        activeChanel: PlaylistChanelEnum.VISUAL,
        checkboxSelectedStripe: null, // only chosen by checkbox
        checkboxSelectedVisual: [],
        checkboxSelectedAudio: [],
        stripeToUpdate: null, // for stripe that came from DB and has been updated
    }
})

@Injectable()
export class ManagePlaylistsState extends NgxsDataRepository<ManagePlaylistsStateModel> {
    constructor() {
        super();
    }

    @Selector([ManagePlaylistsState])
    static getDroppedAudio(state: ManagePlaylistsStateModel): IPlaylistMedia[] {
        return state.droppedAudioMedia;
    }

    @Selector([ManagePlaylistsState])
    static getDroppedVisual(state: ManagePlaylistsStateModel): IPlaylistMedia[] {
        return state.droppedVisualMedia;
    }

    @Selector([ManagePlaylistsState])
    static getDroppedStripe(state: ManagePlaylistsStateModel): IPlaylistStripe | null {
        return state.droppedStripe;
    }

    @DataActionWithoutSubscription()
    onDropVisualContainerEvent(event: CdkDragDrop<Array<IPlaylistMedia | IPlaylistStripe>>): void {
        const stripe: IPlaylistStripe | null = event.item.data.hasOwnProperty('stripe') ? event.item.data : null;
        const widget = this.getWidgetMedia(event.item.data);
        const visualContent = [...this.ctx.getState().droppedVisualMedia]; // array that has mutation below

        if (stripe) {
            this.ctx.patchState({droppedStripe: stripe});
            return;
        } // stripe exist only in a single variant and has last position in container

        if (widget) {
            const arr = PlaylistModel.setWidgetIntoVisualContainer(visualContent, widget);
            this.ctx.patchState({droppedVisualMedia: arr});
            return;
        }

        if (event.previousContainer === event.container) {
            moveItemInArray(
                visualContent,
                event.previousIndex,
                event.currentIndex,
            );
        } else {
            copyArrayItem(
                event.previousContainer.data,
                visualContent,
                event.previousIndex,
                event.currentIndex
            );
        }
        const orderedContent = PlaylistModel.setVisualMediaOrder(visualContent);
        this.ctx.patchState({droppedVisualMedia: orderedContent});
    }

    @DataActionWithoutSubscription()
    onDropAudioContainerEvent(event: CdkDragDrop<Array<IPlaylistMedia>>): void {
        const audioContent = [...this.ctx.getState().droppedAudioMedia];
        if (event.previousContainer === event.container) {
            moveItemInArray(
                audioContent,
                event.previousIndex,
                event.currentIndex,
            );
        } else {
            copyArrayItem(
                event.previousContainer.data,
                audioContent,
                event.previousIndex,
                event.currentIndex
            );
        }
        const orderedContent = PlaylistModel.setOrderPlaylistMedia(audioContent);
        this.ctx.patchState({droppedAudioMedia: orderedContent});
    }

    @DataActionWithoutSubscription() changeChanel(chanel: PlaylistChanelEnum): void {
        this.ctx.patchState({activeChanel: chanel});
    }

    @DataActionWithoutSubscription()
    setStripeByCheckbox(event: boolean, stripe: IPlaylistStripe): void {
        if (event) this.ctx.patchState({checkboxSelectedStripe: stripe});
        else this.ctx.patchState({checkboxSelectedStripe: null});
    }

    @DataActionWithoutSubscription()
    setVisualByCheckbox(event: boolean, media: IPlaylistMedia): void {
        const checkBoxed = this.ctx.getState().checkboxSelectedVisual;
        if (event) this.ctx.patchState({checkboxSelectedVisual: [media, ...checkBoxed]});
        else {
            const filtered = checkBoxed.filter((plMedia) => plMedia.media.id !== media.media.id);
            this.ctx.patchState({checkboxSelectedVisual: filtered});
        }
    }

    @DataActionWithoutSubscription()
    setAudioByCheckbox(event: boolean, media: IPlaylistMedia): void {
        const checkBoxed = this.ctx.getState().checkboxSelectedAudio;
        if (event) this.ctx.patchState({checkboxSelectedAudio: [media, ...checkBoxed]});
        else {
            const filtered = checkBoxed.filter((plMedia) => plMedia.media.id !== media.media.id);
            this.ctx.patchState({checkboxSelectedAudio: filtered});
        }
    }

    @DataActionWithoutSubscription()
    replaceEditedStripe(stripe: IPlaylistStripe): void {
        this.ctx.patchState({droppedStripe: stripe});
        this.setStripeToUpdate(stripe);
    }

    @DataActionWithoutSubscription()
    replaceEditedVisualMedia(media: IPlaylistMedia): void {
        const visualMedia = [...this.ctx.getState().droppedVisualMedia];
        const arrayPosition = visualMedia.findIndex((plMedia) => plMedia.sequence === media.sequence);
        visualMedia.splice(arrayPosition, 1, media);
        this.ctx.patchState({droppedVisualMedia: visualMedia});
    }

    @DataActionWithoutSubscription()
    deleteFromVisualContainer(targetMedia: IPlaylistMedia): void {
        const currentVisual = this.ctx.getState().droppedVisualMedia;
        const filtered = currentVisual.filter((plM) => plM.sequence !== targetMedia.sequence);
        this.ctx.patchState({droppedVisualMedia: PlaylistModel.setOrderPlaylistMedia(filtered)});
    }

    @DataActionWithoutSubscription()
    deleteStripeFromContainer(): void {
        this.ctx.patchState({droppedStripe: null});
    }

    @DataActionWithoutSubscription()
    deleteFromAudioContainer(plMedia: IPlaylistMedia): void {
        const currentAudio = this.ctx.getState().droppedAudioMedia;
        const filtered = currentAudio.filter((plM) => plM.sequence !== plMedia.sequence);
        this.ctx.patchState({droppedAudioMedia: PlaylistModel.setOrderPlaylistMedia(filtered)});
    }

    @DataActionWithoutSubscription()
    pushCheckBoxedIntoDropped(): void {
        const state = this.ctx.getState();
        let visual = PlaylistModel.setVisualMediaOrder([...state.checkboxSelectedVisual, ...state.droppedVisualMedia]);
        const audio = PlaylistModel.setOrderPlaylistMedia([...state.checkboxSelectedAudio, ...state.droppedAudioMedia]);
        const stripe = state.checkboxSelectedStripe || state.droppedStripe;
        const widget = visual.find((media) => media.media.mediaType === MediaTypeEnum.widget);

        if (widget) {
            visual = PlaylistModel.setWidgetIntoVisualContainer([...state.droppedVisualMedia, ...state.checkboxSelectedVisual], widget);
        }

        this.ctx.patchState({droppedAudioMedia: audio, droppedStripe: stripe, droppedVisualMedia: visual});
        // clear checkBoxed
        this.clearCheckBoxedMedia();
    }

    @DataActionWithoutSubscription()
    clearCheckBoxedMedia(): void {
        this.ctx.patchState({checkboxSelectedStripe: null, checkboxSelectedVisual: [], checkboxSelectedAudio: []});
    }

    @DataActionWithoutSubscription()
    setPlaylistMediaFromDB(plMedia: IPlaylistMedia[]): void {
        const visual = plMedia.filter((media) => media.media.mediaType !== MediaTypeEnum.audio);
        const audio = plMedia.filter((media) => media.media.mediaType === MediaTypeEnum.audio);
        this.ctx.patchState({
            droppedVisualMedia: PlaylistModel.getOrderedPlaylistMedia(visual),
            droppedAudioMedia: PlaylistModel.getOrderedPlaylistMedia(audio)
        });
    }

    @DataActionWithoutSubscription()
    setPlaylistStripeFromDB(stripe: IPlaylistStripe): void {
        this.ctx.patchState({droppedStripe: stripe});
    }

    private getWidgetMedia(el: IPlaylistMedia | IPlaylistStripe): IPlaylistMedia | null {
        const item: IPlaylistMedia | null = el.hasOwnProperty('media') ? el as IPlaylistMedia : null;
        if (item && item.media.mediaType === MediaTypeEnum.widget) return item;
        return null;
    }

    // only for stripe from DB that has been updated
    private setStripeToUpdate(stripe: IPlaylistStripe): void {
        const stripeToUpdate = stripe.hasOwnProperty('id') ? stripe : null;
        if (stripeToUpdate) this.ctx.patchState({stripeToUpdate});
    }
}
